/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Menu } from './styles';

export interface KebabMenuItem {
  label: string;
  onClick: () => void;
  danger?: boolean;
  dividerBefore?: boolean;
}

interface AiManagementCardMenuProps {
  items: KebabMenuItem[];
  ariaLabel: string;
  label?: string;
  disabled?: boolean;
}

const AiManagementCardMenu: React.FC<AiManagementCardMenuProps> = ({
  items,
  ariaLabel,
  label,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ x: number; y: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setCoords({ x: rect.right, y: rect.bottom + 4 });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideMenu = menuRef.current?.contains(target);
      const insideButton = buttonRef.current?.contains(target);
      if (!insideMenu && !insideButton) setOpen(false);
    };
    const handleScroll = () => setOpen(false);
    document.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open]);

  const runAction = (action: () => void) => (event: React.MouseEvent) => {
    event.stopPropagation();
    setOpen(false);
    action();
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={
          label
            ? 'btn btn-secondary btn-sm d-flex align-items-center'
            : 'btn btn-link btn-sm text-dark p-0 d-flex align-items-center'
        }
        aria-label={ariaLabel}
        aria-expanded={open}
        disabled={disabled}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={toggleMenu}
      >
        {label}
        <i className="material-icons">{label ? 'arrow_drop_down' : 'more_vert'}</i>
      </button>

      {open &&
        coords &&
        createPortal(
          <Menu
            ref={menuRef}
            className="dropdown-menu show"
            style={{
              position: 'fixed',
              left: coords.x,
              top: coords.y,
              transform: 'translateX(-100%)',
              zIndex: 3000000002,
            }}
          >
            {items.map((item) => (
              <React.Fragment key={item.label}>
                {item.dividerBefore && <div className="dropdown-divider" />}
                <button
                  type="button"
                  className={`dropdown-item ${item.danger ? 'text-danger' : ''}`}
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={runAction(item.onClick)}
                >
                  {item.label}
                </button>
              </React.Fragment>
            ))}
          </Menu>,
          document.body,
        )}
    </>
  );
};

export default AiManagementCardMenu;
